/**
 * The team's draft as the page edits it: the server copy plus unsent local edits, saved by `patchDraft` (screens §P6 Saving).
 * Decision and thesis edits debounce 400 ms; rationales save at once (their callers debounce typing). Writes go one at a time with
 * If-Match = the last version seen; a 412 adopts the server copy and re-sends, so nothing typed is lost.
 * An edit stays in the unsent set until the server confirms it (only the values that were sent and are still current are then
 * cleared); a refused or failed save keeps it, marks its row "not saved" and waits for "Try again" (a lost connection retries by
 * itself). The set is mirrored in the browser so a reload in the window still has it, and is sent when the page is hidden.
 * A save does not wait for the risk projection (an engine run): its reply carries the kept one or none. When the saved version has none and the edit lane has drained,
 * the draft is read once, which builds it, and the figures that print it say "Updating…" until then (`useProjectionPending`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { decisionOfPath, withDecisionPath } from "@bsim/domain";
import type { BodyOf, DraftView, ErrorBody } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import type { ApiResult } from "../api/types";
import { setProjectionPending } from "../api/riskFields";

export type PatchBody = BodyOf<"patchDraft">;
const DEBOUNCE_MS = 400;
/** A save that did not reach the server (offline, a 5xx) is tried again after these waits, the last one repeating. */
export const RETRY_BACKOFF_MS: readonly number[] = [1000, 2000, 4000, 8000];
const transient = (status: number): boolean => status === 0 || status >= 500;
/** A refusal the server will give again for the same body (a step limit, a validation, a card hold): sending it again can never save it, so the edit is put back instead (a lock, a pause, a sign-in or a closed round clear by themselves: those wait for "Try again"). */
const REFUSALS: ReadonlySet<string> = new Set(["LEVER_NOT_IN_LEVEL", "VALIDATION", "LEVER_CONSTRAINED"]);
/** A write that meets a newer server copy (412) is sent again with the new version, at most this many times in a row. */
const MAX_RESENDS = 5;
/** One lane per draft and api client: every writer for the draft (this page, or the copy of it a remount leaves still saving) goes through one chain, so only one save is ever in flight, and each reads the newest version any of them saw. */
interface Lane { tail: Promise<unknown>; latest: DraftView | null }
const lanes = new WeakMap<ApiClient, Map<string, Lane>>();
const laneOf = (api: ApiClient, key: string): Lane => {
  let byKey = lanes.get(api);
  if (byKey === undefined) { byKey = new Map(); lanes.set(api, byKey); }
  let l = byKey.get(key);
  if (l === undefined) { l = { tail: Promise.resolve(), latest: null }; byKey.set(key, l); }
  return l;
};
const mirrorKey = (teamId: string, roundId: string): string => `bsim.draft.unsent.${teamId}.${roundId}`;
/** What an edit was made against: the server's value at each edited path when it was first edited. Kept with the edit, and in the browser, so a newer value from someone else is never overwritten by an old edit. */
type From = Readonly<Record<string, unknown>>;
interface Mirror { readonly body: PatchBody; readonly from: From }
const readMirror = (key: string): Mirror => {
  const empty: Mirror = { body: {}, from: {} };
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return empty;
    const m = JSON.parse(raw) as Partial<Mirror> | null;
    const ok = typeof m === "object" && m !== null && typeof m.body === "object" && m.body !== null && typeof m.from === "object" && m.from !== null
      && (m.body.decisions === undefined || (typeof m.body.decisions === "object" && m.body.decisions !== null)) && (m.body.thesis === undefined || typeof m.body.thesis === "string");
    if (ok) return m as Mirror;
    window.localStorage.removeItem(key);
  } catch { try { window.localStorage.removeItem(key); } catch { /* no storage */ } }
  return empty;
};
const writeMirror = (key: string, m: Mirror): void => {
  try { if (Object.keys(m.body).length === 0) window.localStorage.removeItem(key); else window.localStorage.setItem(key, JSON.stringify(m)); } catch { /* no storage: the in-memory set still holds */ }
};
const valueAt = (view: DraftView, path: string): unknown => path === "thesis" ? view.thesis.text : path.split(".").reduce<unknown>((o, k) => (o as Record<string, unknown> | undefined)?.[k], view.decisions);
const editOf = (body: PatchBody, path: string): unknown => path === "thesis" ? body.thesis : body.decisions?.[path as never];
/** Sorts the edits (with the server value each started from) against the server copy: `moved` paths were changed by someone else to a value other than the edit, so the edit is not sent; `committed` paths already hold the edit (a save that landed but whose reply was lost), so they need no sending and no note. */
const movedOn = (view: DraftView, from: From, edits: PatchBody): { moved: string[]; committed: string[] } => {
  const moved: string[] = [], committed: string[] = [];
  for (const p of Object.keys(from)) {
    const now = JSON.stringify(valueAt(view, p));
    if (now === JSON.stringify(from[p])) continue;
    (now === JSON.stringify(editOf(edits, p)) ? committed : moved).push(p);
  }
  return { moved, committed };
};
const MOVED_ON_NOTE = "An edit made before the reload was not saved; the plan moved on.";
/** `body` without the given paths. */
const omit = (body: PatchBody, paths: readonly string[]): PatchBody => {
  const decisions = Object.fromEntries(Object.entries(body.decisions ?? {}).filter(([p]) => !paths.includes(p)));
  return { ...(Object.keys(decisions).length > 0 ? { decisions: decisions as never } : {}), ...(body.thesis !== undefined && !paths.includes("thesis") ? { thesis: body.thesis } : {}) };
};
const pathsOf = (b: PatchBody): string[] => [...Object.keys(b.decisions ?? {}), ...(b.thesis === undefined ? [] : ["thesis"])];
/** What is left of `unsent` once `sent` is confirmed: a path stays when it was edited again since. */
const without = (unsent: PatchBody, sent: PatchBody): PatchBody => {
  const decisions = Object.fromEntries(Object.entries(unsent.decisions ?? {}).filter(([path, v]) => !(path in (sent.decisions ?? {}) && JSON.stringify(sent.decisions?.[path as never]) === JSON.stringify(v))));
  return {
    ...(Object.keys(decisions).length > 0 ? { decisions: decisions as never } : {}),
    ...(unsent.thesis !== undefined && unsent.thesis !== sent.thesis ? { thesis: unsent.thesis } : {}),
  };
};

const merge = (a: PatchBody, b: PatchBody): PatchBody => ({
  ...a, ...b,
  ...(a.decisions !== undefined || b.decisions !== undefined ? { decisions: { ...a.decisions, ...b.decisions } } : {}),
  ...(a.rationales !== undefined || b.rationales !== undefined ? { rationales: { ...a.rationales, ...b.rationales } } : {}),
});

/** Edits the server refused and the page put back: the decisions they were on, and the server's reason. Cleared by the next edit to one of those decisions. */
export interface Refusal { readonly decisionIds: readonly string[]; readonly error: ErrorBody["error"] }

export interface DraftEditor {
  /** Server draft with unsent decision and thesis edits applied. */
  readonly draft: DraftView;
  /** The server copy alone (what is saved). */
  readonly saved: DraftView;
  readonly savedAt: Date;
  readonly error: ErrorBody["error"] | null;
  /** The decision paths (and "thesis") whose last save failed and is waiting to be tried again: their rows read "not saved". */
  readonly notSaved: readonly string[];
  /** Every decision path (and "thesis") whose edit the server has not confirmed yet: the row says so; the preview does not follow it (it runs on `saved`). */
  readonly unconfirmed: readonly string[];
  /** The decision paths of `unconfirmed` (no thesis): what the forecast is waiting on. */
  readonly pendingDecisions: readonly string[];
  /** An edit the server refused and the page put back to the saved value (null when none). */
  readonly refusal: Refusal | null;
  /** The paths still unconfirmed right now (a ref read, for a gate that must not wait for a render). */
  readonly unconfirmedNow: () => readonly string[];
  /** Sends the edits that failed again, now. */
  readonly retry: () => void;
  /** Set when an edit was dropped because the plan moved on under it (someone else changed the same decision): said on the page. */
  readonly droppedNote: string | null;
  /** Debounced: decisions and thesis. */
  readonly edit: (body: Pick<PatchBody, "decisions" | "thesis">) => void;
  /** Immediate, in order with every other write: a rationale, or the guided body's decision edit. Resolves with the result so the caller can show a refusal; the saved stamp moves with it. */
  readonly write: (body: PatchBody) => Promise<ApiResult<DraftView>>;
  /** A newer copy from elsewhere (a lock claim, a refetch): adopted when its version is not older. */
  readonly adopt: (next: DraftView) => void;
  /** Sends any debounced edit now and waits for every write: the saved draft a next step (send to the CRO, review) must act on. */
  readonly settle: () => Promise<DraftView>;
}

export function useDraft(api: ApiClient, teamId: string, initial: DraftView): DraftEditor {
  const queryClient = useQueryClient();
  const key = mirrorKey(teamId, initial.roundId);
  const [server, setServer] = useState(initial);
  const restored = useRef<{ mirror: Mirror; gone: string[] } | null>(null);
  if (restored.current === null) {
    const mirror = readMirror(key);
    const { moved: gone, committed } = movedOn(initial, mirror.from, mirror.body);
    const dropped = [...gone, ...committed];
    restored.current = { mirror: { body: omit(mirror.body, dropped), from: Object.fromEntries(Object.entries(mirror.from).filter(([p]) => !dropped.includes(p))) }, gone };
    if (dropped.length > 0) writeMirror(key, restored.current.mirror);
  }
  const [unsent, setUnsent] = useState<PatchBody>(restored.current.mirror.body);
  const [droppedNote, setDroppedNote] = useState<string | null>(restored.current.gone.length > 0 ? MOVED_ON_NOTE : null);
  const fromRef = useRef<From>(restored.current.mirror.from);
  const [writeError, setWriteError] = useState<ErrorBody["error"] | null>(null);
  const [flushError, setFlushError] = useState<ErrorBody["error"] | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  /** The body of the save running now: a revert of a path in it is still an edit (the save lands first, then the revert is sent). */
  const inflight = useRef<PatchBody>({});
  const serverRef = useRef(server);
  const unsentRef = useRef<PatchBody>(unsent);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lane = laneOf(api, key);

  const setUnsentBoth = useCallback((next: PatchBody) => {
    unsentRef.current = next; setUnsent(next);
    fromRef.current = Object.fromEntries(Object.entries(fromRef.current).filter(([p]) => pathsOf(next).includes(p)));
    writeMirror(key, { body: next, from: fromRef.current });
  }, [key]);

  const adopt = useCallback((next: DraftView) => {
    if (next.version < serverRef.current.version) return;
    serverRef.current = next;
    setServer(next);
  }, []);
  useEffect(() => adopt(initial), [initial, adopt]);

  // The saved version has no projection: one read of the draft once the lane is idle fills it (the server builds it for a read). A read that comes back with none (nothing to price) ends the wait for that version.
  const [readFor, setReadFor] = useState<number | null>(null);
  const missing = server.riskProjection === undefined;
  const idle = pathsOf(unsent).length === 0;
  const pending = missing && readFor !== server.version;
  useEffect(() => { setProjectionPending(teamId, pending); }, [teamId, pending]);
  useEffect(() => () => setProjectionPending(teamId, false), [teamId]);
  useEffect(() => {
    if (!missing || !idle || readFor === server.version) return;
    const { version, roundId } = serverRef.current;
    let live = true;
    void (async () => {
      let seen: Promise<unknown>;
      do { seen = lane.tail; await seen; } while (seen !== lane.tail);
      if (!live || serverRef.current.version !== version || pathsOf(unsentRef.current).length > 0) return;
      const read = await api.call("getDraft", { teamId, roundId }, undefined).catch(() => null); // a failed read ends the wait: the figures stay without, and the next save asks again
      if (!live) return;
      if (read?.ok === true) {
        adopt(read.value);
        queryClient.setQueriesData({ queryKey: ["teamState", { teamId }] }, (old: unknown) => (typeof old === "object" && old !== null && "draft" in old && (old as { draft: DraftView | null }).draft !== null && (old as { draft: DraftView }).draft.version <= read.value.version ? { ...old, draft: read.value } : old));
      }
      setReadFor(version);
    })();
    return () => { live = false; };
  }, [missing, idle, readFor, server.version, api, teamId, adopt, queryClient, lane]);

  /** One write. A 412 adopts the server copy and sends the same body again, as many times as another writer gets in between. */
  const send = useCallback(async (first: PatchBody, rebase?: () => PatchBody): Promise<ApiResult<DraftView>> => {
    let body = first;
    for (let attempt = 0; ; attempt += 1) {
      // A save that finished meanwhile (an earlier copy of this page) may have moved the version on: send on the newest.
      if (lane.latest !== null && lane.latest.version > serverRef.current.version) { serverRef.current = lane.latest; setServer(lane.latest); }
      const result = await api.call("patchDraft", { teamId, roundId: serverRef.current.roundId }, body, { "if-match": `"${serverRef.current.version}"` });
      if (result.ok) {
        if (lane.latest === null || result.value.version >= lane.latest.version) lane.latest = result.value;
        adopt(result.value);
        // The team state carries the same draft (the steps still to sign, the changed desks and the reasons owed follow its server-worked lists): it takes the saved copy now instead of a read.
        queryClient.setQueriesData({ queryKey: ["teamState", { teamId }] }, (old: unknown) => (typeof old === "object" && old !== null && "draft" in old && (old as { draft: DraftView | null }).draft !== null && (old as { draft: DraftView }).draft.version <= result.value.version ? { ...old, draft: result.value } : old));
        return result;
      }
      const current = result.status === 412 ? (result.current as DraftView | undefined) : undefined;
      if (current !== undefined && attempt < MAX_RESENDS) {
        serverRef.current = current; setServer(current);
        if (lane.latest === null || current.version >= lane.latest.version) lane.latest = current;
        // Edits to a decision the newer copy changed are not sent over it: they are dropped and said so.
        if (rebase !== undefined) { body = rebase(); if (pathsOf(body).length === 0) return { ok: true, value: current } as ApiResult<DraftView>; }
        continue;
      }
      // A card holds this decision: the server copy (with the hold) is adopted; the refusal itself is handled like any other (the edit goes back, one notice says why).
      if (result.error.code === "LEVER_CONSTRAINED" && result.current !== undefined) { serverRef.current = result.current as DraftView; setServer(result.current as DraftView); }
      void queryClient.invalidateQueries({ queryKey: ["teamState"] });
      return result;
    }
  }, [api, teamId, adopt, queryClient, lane]);

  const enqueue = useCallback(<T,>(job: () => Promise<T>): Promise<T> => {
    const run = lane.tail.then(job);
    const tail = run.catch(() => undefined);
    lane.tail = tail;
    void tail.then(() => { if (lane.tail === tail) lane.tail = Promise.resolve(); });
    return run;
  }, [lane]);

  const write = useCallback((body: PatchBody) => enqueue(async () => {
    const r = await send(body);
    setWriteError(r.ok ? null : r.error);
    return r;
  }), [enqueue, send]);

  const failures = useRef(0);
  const [failed, setFailed] = useState<readonly string[]>([]);
  const flushRef = useRef<() => void>(() => undefined);
  /** Sends the unsent edits as they stand when their turn comes; clears only what the server confirmed. */
  const flush = useCallback(() => {
    clearTimeout(timer.current);
    void enqueue(async () => {
      const body = unsentRef.current;
      if (pathsOf(body).length === 0) { setFailed([]); return; }
      const rebase = (): PatchBody => {
        const { moved, committed } = movedOn(serverRef.current, fromRef.current, merge(unsentRef.current, body));
        if (moved.length === 0 && committed.length === 0) return body;
        if (moved.length > 0) setDroppedNote(MOVED_ON_NOTE);
        // A committed path is kept only if it was edited again since (then it rests on what the server now holds).
        const again = committed.filter((p) => JSON.stringify(editOf(unsentRef.current, p)) !== JSON.stringify(editOf(body, p)));
        setUnsentBoth(omit(unsentRef.current, [...moved, ...committed.filter((p) => !again.includes(p))]));
        for (const p of again) fromRef.current = { ...fromRef.current, [p]: valueAt(serverRef.current, p) };
        return omit(body, [...moved, ...committed]);
      };
      inflight.current = body;
      const r = await send(body, rebase);
      inflight.current = {};
      if (r.ok) {
        failures.current = 0;
        setFlushError(null);
        const rest = without(unsentRef.current, body);
        // What is still waiting was edited again meanwhile: it now rests on what this save left on the server.
        fromRef.current = Object.fromEntries(pathsOf(rest).map((p) => [p, valueAt(serverRef.current, p)]));
        setUnsentBoth(rest);
        setFailed([]);
        if (pathsOf(unsentRef.current).length > 0) flushRef.current();
        return;
      }
      if (REFUSALS.has(r.error.code)) {
        // A refusal is final for this body: the refused decisions go back to the saved value (controls, preview and Review all stay on it), one notice says why, and the rest is sent on.
        const details = r.error.details as { decisionIds?: readonly string[]; limits?: readonly { decisionId: string }[] } | undefined;
        const named = new Set(details?.decisionIds ?? details?.limits?.map((l) => l.decisionId) ?? []);
        const refused = pathsOf(body).filter((p) => p !== "thesis" && (named.size === 0 || named.has(decisionOfPath(p))));
        setUnsentBoth(omit(unsentRef.current, refused.filter((p) => body.decisions?.[p as never] === unsentRef.current.decisions?.[p as never])));
        setRefusal({ decisionIds: [...new Set(refused.map(decisionOfPath))], error: r.error });
        setFailed([]);
        failures.current = 0;
        if (pathsOf(unsentRef.current).length > 0) flushRef.current();
        return;
      }
      // Not saved: the edit stays, its row says so. A lost connection tries again by itself; a lock or pause waits for "Try again".
      setFlushError(r.error);
      setFailed(pathsOf(body));
      if (transient(r.status)) {
        timer.current = setTimeout(flushRef.current, RETRY_BACKOFF_MS[Math.min(failures.current, RETRY_BACKOFF_MS.length - 1)]);
        failures.current += 1;
      }
    });
  }, [enqueue, send, setUnsentBoth]);
  flushRef.current = flush;

  const edit = useCallback<DraftEditor["edit"]>((body) => {
    // Remember what the server held at each path the first time it is edited.
    setRefusal((r) => (r !== null && Object.keys(body.decisions ?? {}).some((p) => r.decisionIds.includes(decisionOfPath(p))) ? null : r));
    fromRef.current = { ...Object.fromEntries(pathsOf(body).filter((p) => !(p in fromRef.current)).map((p) => [p, valueAt(serverRef.current, p)])), ...fromRef.current };
    const merged = merge(unsentRef.current, body);
    // An edit back to the saved value needs no save, and no failure of its own stays: unless a save carrying the path is running (it lands first, so the revert must follow it).
    const back = Object.entries(body.decisions ?? {}).filter(([p, v]) => JSON.stringify(valueAt(serverRef.current, p)) === JSON.stringify(v) && !(p in (inflight.current.decisions ?? {}))).map(([p]) => p);
    setUnsentBoth(back.length === 0 ? merged : omit(merged, back));
    if (back.length > 0) setFailed((f) => f.filter((p) => !back.includes(p)));
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, DEBOUNCE_MS);
  }, [flush, setUnsentBoth]);

  const retry = useCallback(() => { failures.current = 0; flush(); }, [flush]);

  // Edits restored from the browser (a reload in the window) are sent at once; a page being hidden sends what is waiting.
  useEffect(() => { if (pathsOf(unsentRef.current).length > 0) flushRef.current(); }, []);
  useEffect(() => {
    const hide = () => { if (pathsOf(unsentRef.current).length > 0) flushRef.current(); };
    const onVisibility = () => { if (document.visibilityState === "hidden") hide(); };
    window.addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { window.removeEventListener("pagehide", hide); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);
  useEffect(() => () => { clearTimeout(timer.current); if (pathsOf(unsentRef.current).length > 0) flushRef.current(); }, []);

  const settle = useCallback(async () => {
    clearTimeout(timer.current);
    if (pathsOf(unsentRef.current).length > 0) flush();
    let seen: Promise<unknown>;
    do { seen = lane.tail; await seen; } while (seen !== lane.tail);
    return serverRef.current;
  }, [flush, lane]);

  // One object per (server copy, unsent edits): the preview feed follows this by identity, and a draft rebuilt on every render is fed back to the provider, which re-renders this page (an update loop).
  const draft = useMemo<DraftView>(() => ({
    ...server,
    decisions: Object.entries(unsent.decisions ?? {}).reduce((d, [path, value]) => withDecisionPath(d, path, value), server.decisions),
    thesis: unsent.thesis === undefined ? server.thesis : { ...server.thesis, text: unsent.thesis },
  }), [server, unsent]);
  const unconfirmed = pathsOf(unsent);
  const notSaved = unconfirmed.filter((p) => failed.includes(p));
  const unconfirmedNow = useCallback(() => pathsOf(unsentRef.current), []);
  // The banner follows the draft: a failed save shows while its edits are still waiting, and goes with them (reverted, or saved).
  const error = writeError ?? (notSaved.length > 0 ? flushError : null);
  return { draft, saved: server, savedAt: new Date(server.updatedAt), error, notSaved, unconfirmed, pendingDecisions: unconfirmed.filter((p) => p !== "thesis"), refusal, unconfirmedNow, retry, droppedNote, edit, write, adopt, settle };
}
